import { isConditionFilter, matchConditionFilter, normalizeConditionFilter, dateSortValue, resolveRelativeDates } from './columnFilterTypes';

// Tokenized text search and column-filter application for list/datasheet pages.
//
// WHY THIS IS SHARED: every dual-view page hand-picked a field whitelist and hand-rolled a
// `field?.toLowerCase().includes(term)` OR-chain, and the picks consistently excluded the
// largest text fields — the rev log searched its one-line summary while ignoring the whole
// changes[] body, so the box could see 9% of the text it was sitting on top of. Several pages
// also wrote the same predicate twice, once for list view and once for the datasheet, which is
// how the two views drifted apart.
//
// THE TWO HALVES BELONG TOGETHER. `matchesSearch` reduces by typed text and `applyColumnFilters`
// reduces by the datasheet's dropdown selections. A page composes both into ONE dataset and
// feeds every view from it, so list view and datasheet view cannot disagree about which rows
// exist. FilterableDataGrid re-applies the same filters to rows already filtered, which is
// idempotent — passing it the composed set is safe and is what makes the views agree.
//
// FILTER SEMANTICS MIRROR THE GRID EXACTLY (FilterableDataGrid getFilteredData): OR within a
// column, AND across columns, comparison against the STRINGIFIED cell value. Any divergence
// here would make the page and the grid disagree about the same filter object, so the
// stringification is deliberate and must stay.

/** Characters that mean "this is HTML", cheap enough to test on every value. */
const LOOKS_LIKE_HTML = /<[a-z/][^>]*>/i;

/**
 * One field's value as searchable text.
 *
 * Arrays are joined rather than skipped — the rev log's `changes` is an array of changelog
 * lines and is the bulk of that page's content. Tags are stripped when a value looks like
 * HTML so that rich-text fields match their prose instead of their markup.
 */
const fieldText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(fieldText).join('\n');
  const str = String(value);
  return LOOKS_LIKE_HTML.test(str) ? str.replace(/<[^>]*>/g, ' ') : str;
};

/**
 * Split a raw query into tokens, honouring double quotes and the OR separator.
 *
 * Token kinds: `{ or: true }` for a bare uppercase OR; `{ prefix, items, negated, raw }` for
 * anything shaped like field:value; `{ text, quoted }` otherwise. A field's value is a comma list
 * whose items may each be quoted, so `owner:"josh neville",danny` is ONE token and a scoped
 * phrase is not split at its space. An unclosed quote takes the rest of the string, which is what
 * someone mid-type expects rather than the term vanishing.
 *
 * OR is case-sensitive on purpose: the word "or" stays an ordinary searchable term.
 */
const SCOPED_ITEM = '(?:"[^"]*"?|[^\\s,"]+)';
const TOKEN_RE = new RegExp(`(-?)(\\w+):(${SCOPED_ITEM}(?:,${SCOPED_ITEM}?)*)|"([^"]*)"?|(\\S+)`, 'g');

/** A comma list with optionally quoted items -> trimmed values. Quotes keep a comma literal. */
export const splitValueList = (list) => {
  const items = [];
  // Leading space is skipped first so `a, "b, c"` (as typed in the builder) still sees the quote.
  const re = /\s*(?:"([^"]*)"?|([^,]+))/g;
  let m;
  while ((m = re.exec(list)) !== null) {
    const v = (m[1] !== undefined ? m[1] : m[2]).trim();
    if (v) items.push(v);
  }
  return items;
};

const tokenize = (raw) => {
  const tokens = [];
  const re = new RegExp(TOKEN_RE.source, 'g');
  let m;
  while ((m = re.exec(raw)) !== null) {
    if (m[2] !== undefined) {
      tokens.push({ prefix: m[2], items: splitValueList(m[3]), negated: m[1] === '-', raw: m[3] });
    } else if (m[4] !== undefined) {
      if (m[4].trim()) tokens.push({ text: m[4].trim(), quoted: true });
    } else if (m[5] === 'OR') {
      tokens.push({ or: true });
    } else {
      tokens.push({ text: m[5], quoted: false });
    }
  }
  return tokens;
};

/** A column label as a field prefix: case-insensitive, spaces removed ("Claude Summary" -> claudesummary). */
export const labelAlias = (label) => String(label || '').toLowerCase().replace(/\s+/g, '');

/**
 * Prefix -> field key. Field keys win over label aliases, so `owner:` means the `owner` field on a
 * page that has one even if another column is labelled "Owner".
 */
const buildPrefixMap = (searchFields = [], labels = []) => {
  const known = new Map();
  labels.forEach(({ key, label }) => { if (key && label) known.set(labelAlias(label), key); });
  searchFields.forEach(f => known.set(f.toLowerCase(), f));
  return known;
};

/**
 * Parse a query string into OR-groups of clauses.
 *
 * Returns `{ groups: [{ clauses: [{ field, values, negated }] }] }`. A row matches when ANY group
 * matches, and a group matches when ALL its clauses do — AND binds tighter than OR, no
 * parentheses. `field` is null for an any-field term. `values` is any-of: `owner:josh,danny`
 * matches either. Every value is a substring match; quotes only keep spaces and commas literal.
 *
 * `searchFields` is required for field:value because an unrecognised prefix must fall back to
 * being an ordinary term — otherwise typing a URL or a time would silently filter everything to
 * nothing. `labels` ([{ key, label }]) lets the column header a user sees work as a prefix too.
 */
export const parseSearchQuery = (raw, searchFields = [], labels = []) => {
  const groups = [{ clauses: [] }];
  if (!raw || !raw.trim()) return { groups };

  const known = buildPrefixMap(searchFields, labels);
  const current = () => groups[groups.length - 1];

  tokenize(raw).forEach(tok => {
    if (tok.or) {
      // Leading, trailing or doubled ORs are typing in progress, not empty groups.
      if (current().clauses.length) groups.push({ clauses: [] });
      return;
    }

    if (tok.prefix !== undefined) {
      const field = known.get(tok.prefix.toLowerCase());
      if (field && tok.items.length) {
        current().clauses.push({ field, values: tok.items, negated: tok.negated });
      } else {
        // Not a searchable field (a URL, a time, a typo): the whole thing is plain text.
        const text = `${tok.prefix}:${tok.raw.replace(/"/g, '')}`;
        current().clauses.push({ field: null, values: [text], negated: tok.negated });
      }
      return;
    }

    let term = tok.text;
    let negated = false;

    // A leading '-' negates, but only on an unquoted token: "-5 offset" searches for the text.
    if (!tok.quoted && term.length > 1 && term.startsWith('-')) {
      negated = true;
      term = term.slice(1);
    }

    if (!term) return;
    current().clauses.push({ field: null, values: [term], negated });
  });

  if (groups.length > 1 && !current().clauses.length) groups.pop();
  return { groups };
};

/** True when a parsed query would narrow anything. */
const hasClauses = (parsed) => parsed.groups.some(g => g.clauses.length);

/**
 * Does one row satisfy the query?
 *
 * Any group may match (OR); within a group every clause must hold (AND). An any-field clause
 * looks across all searchable fields; a scoped clause looks at its one field; a negated clause
 * must find none of its values. An empty query matches everything, so callers can pass the raw
 * box contents without guarding.
 *
 * `searchFields` is an explicit list rather than Object.values(row) on purpose: matching every
 * property means matching record ids, raw FK integers and stringified booleans, which produces
 * hits with no visible cause — typing "fal" would match every row whose boolean is false.
 */
export const matchesSearch = (row, query, searchFields, labels = []) => {
  const parsed = typeof query === 'string' ? parseSearchQuery(query, searchFields, labels) : query;
  if (!hasClauses(parsed)) return true;
  return matchCompiled(row, compileQuery(parsed, searchFields), searchFields);
};

// Per-search preparation done once rather than once per row: needles lowercased, each scoped
// field resolved to its column index. On GF Tags that is ~50K fewer toLowerCase calls a search.
const compileQuery = (parsed, searchFields) => parsed.groups
  .filter(g => g.clauses.length)
  .map(g => g.clauses.map(({ field, values, negated }) => ({
    field,
    index: field ? searchFields.indexOf(field) : -1,
    needles: values.map(v => v.toLowerCase()),
    negated,
  })));

const matchCompiled = (row, compiled, searchFields) => {
  const { texts, joined } = rowTexts(row, searchFields);
  return compiled.some(clauses => clauses.every(({ field, index, needles, negated }) => {
    let haystack = joined;
    if (field) haystack = index >= 0 ? texts[index] : fieldText(row[field]).toLowerCase();
    let hit = false;
    for (let i = 0; i < needles.length && !hit; i++) hit = haystack.includes(needles[i]);
    return hit !== negated;
  }));
};

// Per-row lowercase text, cached. Building it is most of a search's cost (26K rows x 72 columns
// is ~1.9M string conversions), and rows are immutable React state — an edited row is a new
// object — so the cache stays valid until the row or the field list changes. `joined` lets an
// any-field term run one native includes instead of one per column; the separator cannot occur
// in a typed term, so a match never straddles two fields.
const FIELD_SEPARATOR = '\u0001';
const textCache = new WeakMap();
const rowTexts = (row, searchFields) => {
  let cached = textCache.get(row);
  if (!cached || cached.fields !== searchFields) {
    const texts = searchFields.map(f => fieldText(row[f]).toLowerCase());
    cached = { fields: searchFields, texts, joined: texts.join(FIELD_SEPARATOR) };
    textCache.set(row, cached);
  }
  return cached;
};

/**
 * Build the per-row search text ahead of the first search, in small idle-time chunks so a large
 * table (GF Tags: ~0.5 s of work) never blocks a frame. Returns a cancel function for effect
 * cleanup. Without it the first search after a load pays the whole cost.
 */
export const warmSearchCache = (rows, searchFields, chunk = 1000) => {
  if (!rows?.length || !searchFields?.length) return () => {};
  const schedule = typeof window !== 'undefined' && window.requestIdleCallback
    ? cb => window.requestIdleCallback(cb)
    : cb => setTimeout(cb, 0);
  const cancelSchedule = typeof window !== 'undefined' && window.cancelIdleCallback
    ? id => window.cancelIdleCallback(id)
    : id => clearTimeout(id);
  let i = 0;
  let handle = null;
  const step = () => {
    const end = Math.min(i + chunk, rows.length);
    for (; i < end; i++) rowTexts(rows[i], searchFields);
    if (i < rows.length) handle = schedule(step);
  };
  handle = schedule(step);
  return () => cancelSchedule(handle);
};

/**
 * Reduce rows by a typed query. Parses once for the whole set rather than per row.
 */
export const applySearch = (rows, query, searchFields, labels = []) => {
  if (!query || !query.trim() || !searchFields?.length) return rows;
  const parsed = parseSearchQuery(query, searchFields, labels);
  if (!hasClauses(parsed)) return rows;
  const compiled = compileQuery(parsed, searchFields);
  return rows.filter(row => matchCompiled(row, compiled, searchFields));
};

/**
 * The inverse of parseSearchQuery: groups back to the string a user could have typed.
 *
 * Scoped fields are written with their label alias when the page has one (`owner:` rather than
 * `ownerName:`), so the syntax the builder teaches is the syntax that reads naturally. Values are
 * quoted only when they must be. Double quotes inside a value are dropped — the syntax has no
 * escape for them, and a substring match without them finds the same rows in practice.
 */
export const buildSearchQuery = (groups, labels = []) => {
  // An alias is only safe when it parses back to the same field: a label spelled like another
  // field's key (a column "Owner" showing ownerName on a page that also searches `owner`) loses
  // to that key, so such a field is written by its key instead.
  const known = buildPrefixMap(labels.map(l => l.key).filter(Boolean), labels);
  const aliasOf = new Map(labels
    .filter(l => l.key && l.label && known.get(labelAlias(l.label)) === l.key)
    .map(l => [l.key, labelAlias(l.label)]));
  return (groups || [])
    .map(({ clauses }) => (clauses || [])
      .map(({ field, values, negated }) => {
        const clean = (values || []).map(v => String(v).replace(/"/g, '').trim()).filter(Boolean);
        if (!clean.length) return '';
        const neg = negated ? '-' : '';
        if (!field) {
          const v = clean[0];
          const quote = /[\s:]/.test(v) || v.startsWith('-') || v === 'OR';
          return neg + (quote ? `"${v}"` : v);
        }
        const items = clean.map(v => (/[\s,:]/.test(v) ? `"${v}"` : v));
        return `${neg}${aliasOf.get(field) || field}:${items.join(',')}`;
      })
      .filter(Boolean)
      .join(' '))
    .filter(Boolean)
    .join(' OR ');
};

// ── Column filters ───────────────────────────────────────────────────────────────────────────
// A column filter is ONE of two shapes, never both (Excel's model):
//   string[]                       the ticked values — OR within the column
//   { type, join, conds }          one or two typed conditions (utils/columnFilterTypes) — the
//                                  Column Filter's Custom Filter dialog; Phase 4's { op, text }
//                                  still parses as one text condition
// Columns AND together. The server (Services/Search/SqlSearchBuilder.BuildFilters) accepts the
// same JSON, so a server-paged grid and a client-filtered one mean the same thing.

/** Does this column filter narrow anything? */
export const isFilterActive = (f) => (Array.isArray(f) ? f.length > 0 : isConditionFilter(f));

/**
 * Reduce rows by the datasheet's column filters (either shape): value lists OR within a column,
 * conditions per column, columns AND. Pages hand the result to the grid.
 */
export const applyColumnFilters = (rows, filters, now = new Date()) => {
  if (!filters || !Object.keys(filters).length) return rows;
  const active = Object.entries(filters).filter(([, f]) => isFilterActive(f));
  if (!active.length) return rows;
  // `|| ''` not `?? ''`: the grid coerces 0 and false to '' both when building the dropdown
  // options and when matching, so a 0 is offered as the "(empty)" choice. Using ?? would
  // stringify it to '0', and selecting "(empty)" on a numeric column would silently lose rows.
  return rows.filter(row =>
    active.every(([key, f]) => (Array.isArray(f)
      ? f.includes(String(row[key] || ''))
      : matchConditionFilter(row[key], f, now)))
  );
};

/** True when any column filter is currently narrowing the set. */
export const hasColumnFilters = (filters) =>
  !!filters && Object.values(filters).some(isFilterActive);

/**
 * The full reduction a page shows: text search first, then column filters.
 *
 * Callers hand the result to every view AND to the grid as `data`. They must still pass the
 * SEARCH-ONLY set as the grid's `allData`, because the grid builds its dropdown option lists
 * from it — passing the fully filtered set would collapse each dropdown to only the values
 * already selected, making a filter impossible to widen.
 */
export const applySearchAndFilters = (rows, query, searchFields, filters, labels = []) =>
  applyColumnFilters(applySearch(rows, query, searchFields, labels), filters);

/**
 * The query rewritten with field KEYS instead of label aliases (`user:josh` -> `changedBy:josh`),
 * for server-side search: the C# parser (Services/Search/SearchQueryParser) takes keys only, so a
 * page sends this and the server never needs to know the page's column labels.
 */
export const canonicalQuery = (raw, searchFields = [], labels = []) =>
  (raw && raw.trim() ? buildSearchQuery(parseSearchQuery(raw, searchFields, labels).groups, []) : '');

/** Filters as a server request carries them: relative dates resolved to explicit days. */
export const serializeFiltersForServer = (filters, now = new Date()) =>
  serializeFilters(resolveRelativeDates(filters, now));

// ── Sort, filter options, filter URL encoding ────────────────────────────────────────────────
// One comparator for every list and grid: numeric-aware and case-insensitive, so "AI2" sorts
// before "AI10" and "9" before "10" (the grid used to compare lowercased strings, where "10" < "9").
// A shared Collator is much faster than String.localeCompare, which builds locale data per call.
const SORT_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Compare two sort keys: numbers numerically, everything else through the collator. */
export const compareSortValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return SORT_COLLATOR.compare(String(a ?? ''), String(b ?? ''));
};

/**
 * Sort rows by { key, direction: 'asc'|'desc' }. A column may supply sortValue(value) to sort by
 * something other than its text (ranked values, padded versions). Stable: equal keys keep their
 * incoming order, so a secondary order survives. Returns the same row objects (the search text
 * cache is keyed on them). No key or direction = rows unchanged.
 */
export const applySort = (rows, sortConfig, columns = []) => {
  if (!rows?.length || !sortConfig?.key || !sortConfig.direction) return rows;
  const { key } = sortConfig;
  const dir = sortConfig.direction === 'desc' ? -1 : 1;
  const col = columns.find(c => c.key === key);
  // A date column (filterType 'date') sorts chronologically — a timestamp by its instant, a date
  // or a display string like '12/01/2024' by its day; as text they sort wrong. Blank sorts first.
  const valueOf = col?.sortValue ? (row) => col.sortValue(row[key], row)
    : col?.filterType === 'date' ? (row) => dateSortValue(row[key])
    : (row) => row[key];
  return rows
    .map((row, index) => ({ row, index, value: valueOf(row) }))
    .sort((x, y) => compareSortValues(x.value, y.value) * dir || x.index - y.index)
    .map(d => d.row);
};

/**
 * The values a column's filter dropdown offers: every value present once the OTHER columns'
 * filters are applied, so dropdowns cascade but a column's own selection never shrinks its own
 * list (which would make a filter impossible to widen). Same '' coercion as applyColumnFilters,
 * so 0 / false / null all land in the "(empty)" choice.
 */
export const columnOptions = (rows, filters, colKey) => {
  const others = Object.fromEntries(Object.entries(filters || {}).filter(([k]) => k !== colKey));
  const values = new Set(applyColumnFilters(rows || [], others).map(row => String(row[colKey] || '')));
  return [...values].sort(SORT_COLLATOR.compare);
};

// Column filters (either shape) in a URL or a request. JSON keeps values that contain commas or
// colons intact; URLSearchParams handles the percent-encoding. Both directions fail soft — a
// hand-mangled ?filters= must not blank the page — and drop anything malformed.
const cleanFilter = (f) => (Array.isArray(f)
  ? f.map(v => String(v ?? ''))
  : normalizeConditionFilter(f));

export const serializeFilters = (filters) => {
  const active = Object.entries(filters || {}).filter(([, f]) => isFilterActive(f));
  return active.length > 0 ? JSON.stringify(Object.fromEntries(active.map(([k, f]) => [k, cleanFilter(f)]))) : '';
};

export const parseFilters = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, f]) => isFilterActive(f)).map(([k, f]) => [k, cleanFilter(f)])
    );
  } catch {
    return {};
  }
};

/** Sort in a URL as "key" or "-key" (descending); '' when unsorted. */
export const serializeSort = (sortConfig) =>
  (sortConfig?.key && sortConfig.direction ? `${sortConfig.direction === 'desc' ? '-' : ''}${sortConfig.key}` : '');

export const parseSort = (raw) => {
  if (!raw) return null;
  const desc = raw.startsWith('-');
  const key = desc ? raw.slice(1) : raw;
  return key ? { key, direction: desc ? 'desc' : 'asc' } : null;
};

/**
 * The record a list view's detail pane shows, derived from the user's pick and the current trunk.
 *
 * Derived rather than stored: clearing the pick when the search narrows would lose the user's
 * place the moment they back out a typo. Instead the pick survives, and the pane shows it only
 * while the trunk contains it — otherwise the first visible row, and null when nothing matches,
 * so the pane can never display a record the list is not showing.
 *
 * `keep` (pass the page's editing flag) holds a hidden pick on screen so an open edit form is
 * never yanked out from under the user by a keystroke in the search box.
 *
 * The live row from the trunk is returned rather than the stored pick, so a reload after save
 * shows the saved values without a separate sync step.
 */
export const resolveSelection = (rows, picked, { key = 'id', keep = false } = {}) => {
  if (picked) {
    const live = rows.find(r => r[key] === picked[key]);
    if (live) return live;
    if (keep) return picked;
  }
  return rows[0] || null;
};

/** Detail-pane text when the data exists but search and filters leave nothing to show. */
export const NO_MATCH_MESSAGE = 'No records match your search and filters. Try fewer terms, or clear them.';

/** List-panel text in the same situation. One wording on every page, so it can become shared. */
export const NO_MATCH_LIST = 'No matching results';

// Tooltip rather than placeholder: the search inputs are ~180px and would truncate this to
// uselessness, so it hangs off the input's title where it is discoverable without the layout
// fight. Keep it in sync with parseSearchQuery.
export const SEARCH_HINT = [
  'Multiple terms must all match (AND).',
  'OR (uppercase) between terms matches either side.',
  '"quoted phrase" matches the phrase exactly.',
  '-word excludes rows containing it.',
  'field:value searches just that field; field:a,b matches any of them.',
  'Advanced Search in the actions menu builds these for you.',
].join('\n');
